import { useCallback, useEffect } from "react";

import { useHistory } from "react-router";
import { OpenInNewOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Container, Divider, Link, Stack, Typography } from "@mui/material";

import { allocationTxt } from "../../../assets/texts/CloudAnalytics/allocation";
import { EditableResourceHeader } from "../../../Components/EditableResourceHeader/EditableResourceHeader";
import LoadingButton from "../../../Components/LoadingButton";
import SaveDialog from "../../../Components/SaveComponents/SaveDialog";
import { useCustomerContext } from "../../../Context/CustomerContext";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { useHasAllocationWritePermission } from "../hooks/useHasAllocationWritePermission";
import { AllocationDragAndDrop } from "./AllocationDragGroup/AllocationDragGroup";
import useAllocationDndStore from "./hooks/useAllocationDnd";
import { formStoreManager } from "./hooks/useAllocationFormState";
import { useAllocationSave } from "./hooks/useAllocationSave";
import { useAllocationSaveDialog } from "./hooks/useAllocationSaveDialog";
import useAllocationSaveValidation from "./hooks/useAllocationSaveValidation";
import { allocationBaseProps, AllocationPathType, getAllocationPath } from "./utils";

type Props = { edit: true; allocationId: string } | { edit?: false; allocationId?: undefined };

export const Allocation = ({ edit, allocationId }: Props) => {
  const { isError, isSaveAs, isDuplicate, isDirty } = useAllocationSaveValidation({ isEdit: edit });
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });
  const history = useHistory();
  const { customer } = useCustomerContext();

  const {
    items,
    hasUnallocated,
    setHasUnallocated,
    allocationName,
    allocationDescription,
    clear,
    isAllocationEditor,
    setAllocationName,
    setAllocationDescription,
  } = useAllocationDndStore();

  const canEdit = useHasAllocationWritePermission(isAllocationEditor);

  const { saving, handleSave, handleRenameAllocation, handleClose, getWarnings } = useAllocationSave({
    edit,
    allocationId,
    isDuplicate,
    isSaveAs,
  });

  const {
    openSaveDialog,
    handleSaveDialog,
    handleSaveAsDialog,
    handleDialogSave,
    handleDialogClose,
    saveLabel,
    modalText,
  } = useAllocationSaveDialog({
    isSaveAs,
    isDuplicate,
    getWarnings,
    handleSave,
    items,
    hasUnallocated,
    isEdit: edit,
  });

  // Force hasUnallocated to true when multiple items exist
  useEffect(() => {
    if (items.main.length > 1) {
      setHasUnallocated(true);
    }
  }, [items.main.length, setHasUnallocated]);

  // Clear all dnd store on unmount
  useEffect(
    () => () => {
      clear();
      formStoreManager.clear();
    },
    [clear]
  );

  const handleSaveAllocationName = useCallback(
    ({ name, description }: { name: string; description?: string }) => {
      setAllocationName(name);
      setAllocationDescription(description);
      if (edit) {
        handleRenameAllocation({ name, description });
      }
    },
    [edit, handleRenameAllocation, setAllocationDescription, setAllocationName]
  );

  const handleDuplicate = useCallback(() => {
    history.replace({
      pathname: getAllocationPath(customer.id, AllocationPathType.CREATE),
      state: {
        allocationId,
        type: "duplicate",
      },
    });
  }, [allocationId, customer.id, history]);

  return (
    <Box sx={{ position: "relative" }}>
      <Container maxWidth="md" sx={{ pt: 4.5 }}>
        <Stack justifyContent="flex-start" alignItems="flex-start" rowGap={1}>
          <Stack direction="row" alignItems="center">
            <Box sx={{ ml: { xs: "-5px", md: "-40px" } }}>
              <EditableResourceHeader
                name={items.main.length > 1 || hasUnallocated ? allocationName : allocationTxt.ALLOCATION_RULE_LABEL}
                description={allocationDescription}
                onBackButtonClicked={handleClose}
                disabled={(items.main.length === 1 && !hasUnallocated) || !canEdit}
                maxNameLength={50}
                onSave={handleSaveAllocationName}
                resourceName="allocation"
                {...(!edit && { customActionLabel: allocationTxt.ALLOCATION_NAME_CONFIRM })}
              />
            </Box>
          </Stack>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Typography variant="body2" color="text.secondary">
              {allocationTxt.ALLOCATION_DESCRIPTION}
              <Link href="https://help.doit.com/docs/cloud-analytics/allocations" underline="none" target="_blank">
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {allocationTxt.COST_ALLOCATION}
                  <OpenInNewOutlined fontSize="inherit" />
                </Typography>
              </Link>
            </Typography>
          </Box>

          <Alert severity="info" sx={{ width: "100%", display: { xs: "flex", md: "none" } }}>
            {allocationTxt.ALLOCATION_MOBILE_INFO}
          </Alert>
          <AllocationDragAndDrop disabled={saving || !canEdit} />
        </Stack>
      </Container>

      {/* Footer with save buttons */}
      <Box
        sx={{
          position: "fixed",
          display: { xs: "none", md: "block" },
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 980,
          backgroundColor: "background.paper",
        }}
      >
        <Divider />
        <Stack direction="row" alignContent="center" justifyContent="center" spacing={2} p={1}>
          <Container maxWidth="md" sx={{ justifyContent: "flex-end", display: "flex" }}>
            <Button color="primary" onClick={handleClose} disabled={saving} variant="text" size="large" sx={{ mr: 3 }}>
              {allocationTxt.ALLOCATION_CANCEL}
            </Button>
            {canEdit ? (
              <LoadingButton
                color="primary"
                loading={saving}
                onClick={isSaveAs ? handleSaveAsDialog : handleSaveDialog}
                variant="contained"
                mixpanelEventId="allocation.save"
                segmentTrackEvent={trackEvent}
                segmentEventName="Allocation Save Initiated"
                eventProperties={{
                  ...allocationBaseProps,
                  allocationId,
                }}
                data-cy="footer-save-button"
                size="large"
                disabled={isError || !isDirty}
              >
                {saveLabel}
              </LoadingButton>
            ) : (
              <Button
                color="primary"
                onClick={handleDuplicate}
                disabled={saving || !canEdit}
                variant="contained"
                size="large"
                data-cy="footer-duplicate-button"
              >
                {allocationTxt.DUPLICATE}
              </Button>
            )}
          </Container>
        </Stack>
      </Box>

      {/* Save dialog */}
      <SaveDialog
        {...modalText}
        open={openSaveDialog}
        onClose={handleDialogClose}
        value={allocationName}
        onSave={handleDialogSave}
        textFieldProps={{ label: allocationTxt.ALLOCATION_NAME_LABEL }}
      />
    </Box>
  );
};
